import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Inbox,
  FileText,
  Plus,
  Trash2,
  Edit3,
  Eye,
  EyeOff,
  LogOut,
  ArrowUpRight,
  ExternalLink,
  Loader2,
  AlertCircle,
  X,
  Save,
  Check,
} from "lucide-react";
import { api, clearToken, formatApiError } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import usePageTitle from "@/lib/usePageTitle";

// ---------- helpers ----------
const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(d);
  }
};

const emptyArticle = {
  id: null,
  title: "",
  excerpt: "",
  content: "",
  cover_image: "",
  tags: "",
  published: true,
};

// ============================================================
// Article Editor Modal
// ============================================================
const ArticleEditor = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState({
    ...emptyArticle,
    ...initial,
    tags: (initial?.tags || []).join(", "),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const onChange = (f) => (e) =>
    setForm((prev) => ({
      ...prev,
      [f]: e?.target ? e.target.value : e,
    }));

  const save = async () => {
    setError(null);
    if (form.title.trim().length < 3) return setError("Title must be at least 3 characters.");
    if (form.excerpt.trim().length < 10) return setError("Excerpt must be at least 10 characters.");
    if (form.content.trim().length < 20) return setError("Content must be at least 20 characters.");
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      excerpt: form.excerpt.trim(),
      content: form.content,
      cover_image: form.cover_image.trim() || null,
      tags: form.tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      published: !!form.published,
    };
    try {
      if (form.id) {
        await api.put(`/admin/articles/${form.id}`, payload);
      } else {
        await api.post("/admin/articles", payload);
      }
      onSaved();
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Unable to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0A2540]/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4"
      data-testid="article-editor-modal"
    >
      <div className="bg-white w-full max-w-3xl border border-[#E2E8F0] shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E8F0]">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A]">
              {form.id ? "Edit article" : "New article"}
            </div>
            <h3 className="font-serif text-2xl text-[#0A2540]">
              Health Notes
            </h3>
          </div>
          <button
            data-testid="article-editor-close"
            onClick={onClose}
            className="text-[#475569] hover:text-[#0A2540]"
          >
            <X strokeWidth={1.5} />
          </button>
        </div>

        <div className="px-6 py-6 space-y-5">
          <div>
            <label className="label-clean">Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={onChange("title")}
              data-testid="article-input-title"
              className="input-clean"
              placeholder="e.g. Why your annual blood work matters more than you think"
            />
          </div>

          <div>
            <label className="label-clean">Excerpt (1-2 sentences) *</label>
            <textarea
              rows={2}
              value={form.excerpt}
              onChange={onChange("excerpt")}
              data-testid="article-input-excerpt"
              className="input-clean resize-y"
              placeholder="Short summary shown on the listing page."
            />
          </div>

          <div>
            <label className="label-clean">
              Content (supports Markdown / plain text) *
            </label>
            <textarea
              rows={12}
              value={form.content}
              onChange={onChange("content")}
              data-testid="article-input-content"
              className="input-clean resize-y font-serif text-base leading-[1.75]"
              placeholder={"# Heading\n\nWrite your article here…\n\n## Subheading\n\nAdd more details and paragraphs."}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label-clean">Cover image URL (optional)</label>
              <input
                type="url"
                value={form.cover_image}
                onChange={onChange("cover_image")}
                data-testid="article-input-cover"
                className="input-clean"
                placeholder="https://…"
              />
            </div>
            <div>
              <label className="label-clean">Tags (comma-separated)</label>
              <input
                type="text"
                value={form.tags}
                onChange={onChange("tags")}
                data-testid="article-input-tags"
                className="input-clean"
                placeholder="preventive-health, women, screening"
              />
            </div>
          </div>

          <label className="inline-flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, published: e.target.checked }))
              }
              data-testid="article-input-published"
              className="h-4 w-4 accent-[#0A2540]"
            />
            <span className="font-sans text-sm text-[#0A2540]">
              Publish immediately (uncheck to save as draft)
            </span>
          </label>

          {error && (
            <div
              data-testid="article-editor-error"
              className="flex items-start gap-3 border border-red-200 bg-red-50 px-4 py-3 font-sans text-sm text-red-700"
            >
              <AlertCircle size={16} strokeWidth={1.5} className="mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#E2E8F0] bg-[#F8FAFC]">
          <button
            onClick={onClose}
            data-testid="article-editor-cancel"
            className="px-6 py-3 font-mono text-[10px] uppercase tracking-widest-plus text-[#475569] hover:text-[#0A2540]"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="article-editor-save"
            className="btn-primary disabled:opacity-60"
          >
            {saving ? (
              <>
                <Loader2 size={14} strokeWidth={1.8} className="animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Save size={14} strokeWidth={1.5} />
                {form.id ? "Update article" : "Publish article"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Dashboard Page
// ============================================================
export default function AdminDashboardPage() {
  usePageTitle("Admin Dashboard");
  const [tab, setTab] = useState("enquiries");
  const [inquiries, setInquiries] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // article being edited, or {} for new
  const [confirmDelete, setConfirmDelete] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [iRes, aRes] = await Promise.all([
        api.get("/admin/partnerships"),
        api.get("/admin/articles"),
      ]);
      setInquiries(iRes.data);
      setArticles(aRes.data);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Failed to load.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const logout = () => {
    clearToken();
    nav("/admin/login", { replace: true });
  };

  const deleteInquiry = async (id) => {
    try {
      await api.delete(`/admin/partnerships/${id}`);
      setInquiries((list) => list.filter((i) => i.id !== id));
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally {
      setConfirmDelete(null);
    }
  };

  const deleteArticle = async (id) => {
    try {
      await api.delete(`/admin/articles/${id}`);
      setArticles((list) => list.filter((a) => a.id !== id));
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally {
      setConfirmDelete(null);
    }
  };

  const togglePublished = async (article) => {
    try {
      const { data } = await api.put(`/admin/articles/${article.id}`, {
        published: !article.published,
      });
      setArticles((list) => list.map((a) => (a.id === article.id ? data : a)));
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    }
  };

  return (
    <section
      data-testid="admin-dashboard"
      className="min-h-[calc(100vh-5rem)] bg-[#F8FAFC]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 lg:py-14">
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mb-3 inline-flex items-center gap-2">
              <LayoutDashboard size={12} strokeWidth={1.5} />
              Practice Console
            </div>
            <h1 className="font-serif text-4xl md:text-5xl text-[#0A2540] leading-tight">
              Welcome back, Dr. Jain.
            </h1>
            <p className="mt-2 font-sans text-sm text-[#475569]">
              Manage business enquiries and publish new Health Notes.
            </p>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 border border-[#E2E8F0] bg-white hover:border-[#0A2540] px-5 py-3 font-mono text-[10px] uppercase tracking-widest-plus text-[#475569] hover:text-[#0A2540] transition-colors self-start md:self-end"
          >
            <LogOut size={14} strokeWidth={1.5} />
            Sign out
          </button>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          <StatCard
            testId="admin-stat-enquiries"
            label="B2B Enquiries"
            value={inquiries.length}
            hint="Total received"
          />
          <StatCard
            testId="admin-stat-articles"
            label="Health Notes"
            value={articles.length}
            hint={`${articles.filter((a) => a.published).length} live`}
          />
          <StatCard
            testId="admin-stat-drafts"
            label="Drafts"
            value={articles.filter((a) => !a.published).length}
            hint="Not yet published"
          />
        </div>

        <div className="flex flex-wrap items-center gap-1 border-b border-[#E2E8F0] mb-8">
          <TabBtn
            id="enquiries"
            current={tab}
            onClick={setTab}
            icon={Inbox}
            testId="admin-tab-enquiries"
          >
            Business Enquiries ({inquiries.length})
          </TabBtn>
          <TabBtn
            id="articles"
            current={tab}
            onClick={setTab}
            icon={FileText}
            testId="admin-tab-articles"
          >
            Health Notes ({articles.length})
          </TabBtn>
          <TabBtn
            id="bookings"
            current={tab}
            onClick={setTab}
            icon={ArrowUpRight}
            testId="admin-tab-bookings"
          >
            Bookings
          </TabBtn>
        </div>

        {error && (
          <div
            data-testid="admin-error"
            className="mb-6 flex items-start gap-3 border border-red-200 bg-red-50 px-4 py-3 font-sans text-sm text-red-700"
          >
            <AlertCircle size={16} strokeWidth={1.5} className="mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div
            data-testid="admin-loading-content"
            className="py-24 text-center font-mono text-xs uppercase tracking-widest-plus text-[#94A3B8]"
          >
            Loading…
          </div>
        ) : (
          <>
            {tab === "enquiries" && (
              <EnquiriesTab
                inquiries={inquiries}
                onRequestDelete={(i) =>
                  setConfirmDelete({ type: "inquiry", id: i.id, label: i.organization })
                }
              />
            )}
            {tab === "articles" && (
              <ArticlesTab
                articles={articles}
                onNew={() => setEditing({})}
                onEdit={(a) => setEditing(a)}
                onTogglePublished={togglePublished}
                onRequestDelete={(a) =>
                  setConfirmDelete({ type: "article", id: a.id, label: a.title })
                }
              />
            )}
            {tab === "bookings" && <BookingsTab />}
          </>
        )}
      </div>

      {editing !== null && (
        <ArticleEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          label={confirmDelete.label}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() =>
            confirmDelete.type === "inquiry"
              ? deleteInquiry(confirmDelete.id)
              : deleteArticle(confirmDelete.id)
          }
        />
      )}
    </section>
  );
}

// ---------- sub components ----------
const StatCard = ({ label, value, hint, testId }) => (
  <div
    data-testid={testId}
    className="bg-white border border-[#E2E8F0] p-6 flex flex-col justify-between min-h-[140px]"
  >
    <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#94A3B8]">
      {label}
    </div>
    <div className="font-serif text-5xl text-[#0A2540] leading-none">
      {value}
    </div>
    <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A]">
      {hint}
    </div>
  </div>
);

const TabBtn = ({ id, current, onClick, icon: Icon, children, testId }) => {
  const active = current === id;
  return (
    <button
      data-testid={testId}
      onClick={() => onClick(id)}
      className={`inline-flex items-center gap-2 px-4 py-3 font-mono text-[10px] uppercase tracking-widest-plus border-b-2 transition-colors -mb-px ${
        active
          ? "text-[#0A2540] border-[#0A2540]"
          : "text-[#475569] border-transparent hover:text-[#0A2540]"
      }`}
    >
      <Icon size={14} strokeWidth={1.5} />
      {children}
    </button>
  );
};

const EnquiriesTab = ({ inquiries, onRequestDelete }) => {
  if (inquiries.length === 0)
    return (
      <div
        data-testid="admin-enquiries-empty"
        className="bg-white border border-[#E2E8F0] py-16 text-center font-serif italic text-[#94A3B8]"
      >
        No business enquiries yet. New submissions from the B2B page will appear here.
      </div>
    );
  return (
    <div className="bg-white border border-[#E2E8F0]" data-testid="admin-enquiries-list">
      {inquiries.map((i) => (
        <div
          key={i.id}
          data-testid={`admin-enquiry-${i.id}`}
          className="p-6 border-b border-[#E2E8F0] last:border-b-0"
        >
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-baseline gap-3 mb-1">
                <h3 className="font-serif text-xl text-[#0A2540]">
                  {i.organization}
                </h3>
                <span className="font-mono text-[10px] uppercase tracking-widest-plus text-white bg-[#B08A4A] px-2 py-0.5">
                  {i.service_needed}
                </span>
              </div>
              <p className="font-sans text-sm text-[#475569] mb-2">
                <span className="text-[#0A2540]">{i.contact_person}</span>{" "}
                — <a href={`mailto:${i.email}`} className="hover:text-[#2E5AA5]">{i.email}</a>
                {i.phone && (
                  <> · <a href={`tel:${i.phone}`} className="hover:text-[#2E5AA5]">{i.phone}</a></>
                )}
              </p>
              <p className="font-sans text-sm text-[#0A2540] leading-relaxed mt-3 whitespace-pre-wrap">
                {i.message}
              </p>
              <div className="mt-3 font-mono text-[10px] uppercase tracking-widest-plus text-[#94A3B8]">
                Received · {fmtDate(i.created_at)}
              </div>
            </div>
            <button
              onClick={() => onRequestDelete(i)}
              data-testid={`admin-enquiry-delete-${i.id}`}
              className="shrink-0 inline-flex items-center gap-2 border border-[#E2E8F0] hover:border-red-500 hover:text-red-600 px-3 py-2 font-mono text-[10px] uppercase tracking-widest-plus text-[#475569] transition-colors"
            >
              <Trash2 size={12} strokeWidth={1.5} />
              Delete
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const ArticlesTab = ({
  articles,
  onNew,
  onEdit,
  onTogglePublished,
  onRequestDelete,
}) => (
  <>
    <div className="flex items-center justify-between mb-6">
      <p className="font-sans text-sm text-[#475569]">
        Articles publish immediately unless saved as draft.
      </p>
      <button
        onClick={onNew}
        data-testid="admin-new-article-btn"
        className="btn-primary"
      >
        <Plus size={14} strokeWidth={1.5} />
        New Health Note
      </button>
    </div>

    {articles.length === 0 ? (
      <div
        data-testid="admin-articles-empty"
        className="bg-white border border-[#E2E8F0] py-16 text-center font-serif italic text-[#94A3B8]"
      >
        No articles yet. Click <span className="text-[#0A2540]">New Health Note</span> to publish your first one.
      </div>
    ) : (
      <div
        className="bg-white border border-[#E2E8F0]"
        data-testid="admin-articles-list"
      >
        {articles.map((a) => (
          <div
            key={a.id}
            data-testid={`admin-article-${a.id}`}
            className="p-6 border-b border-[#E2E8F0] last:border-b-0"
          >
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-baseline gap-3 mb-2">
                  <h3 className="font-serif text-xl text-[#0A2540]">
                    {a.title}
                  </h3>
                  <span
                    className={`font-mono text-[10px] uppercase tracking-widest-plus px-2 py-0.5 ${
                      a.published
                        ? "bg-[#0A2540] text-white"
                        : "border border-[#B08A4A] text-[#B08A4A]"
                    }`}
                  >
                    {a.published ? "Live" : "Draft"}
                  </span>
                </div>
                <p className="font-sans text-sm text-[#475569] leading-relaxed">
                  {a.excerpt}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {a.tags.map((t) => (
                    <span
                      key={t}
                      className="font-mono text-[9px] uppercase tracking-widest-plus text-[#475569] border border-[#E2E8F0] px-2 py-0.5"
                    >
                      {t}
                    </span>
                  ))}
                </div>
                <div className="mt-3 font-mono text-[10px] uppercase tracking-widest-plus text-[#94A3B8]">
                  {a.slug} · Updated {fmtDate(a.updated_at)}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                {a.published && (
                  <a
                    href={`/health-notes/${a.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`admin-article-view-${a.id}`}
                    className="inline-flex items-center gap-2 border border-[#E2E8F0] hover:border-[#0A2540] px-3 py-2 font-mono text-[10px] uppercase tracking-widest-plus text-[#475569] hover:text-[#0A2540] transition-colors"
                  >
                    <ExternalLink size={12} strokeWidth={1.5} />
                    View
                  </a>
                )}
                <button
                  onClick={() => onTogglePublished(a)}
                  data-testid={`admin-article-toggle-${a.id}`}
                  className="inline-flex items-center gap-2 border border-[#E2E8F0] hover:border-[#0A2540] px-3 py-2 font-mono text-[10px] uppercase tracking-widest-plus text-[#475569] hover:text-[#0A2540] transition-colors"
                >
                  {a.published ? <EyeOff size={12} /> : <Eye size={12} />}
                  {a.published ? "Unpublish" : "Publish"}
                </button>
                <button
                  onClick={() => onEdit(a)}
                  data-testid={`admin-article-edit-${a.id}`}
                  className="inline-flex items-center gap-2 border border-[#E2E8F0] hover:border-[#0A2540] px-3 py-2 font-mono text-[10px] uppercase tracking-widest-plus text-[#475569] hover:text-[#0A2540] transition-colors"
                >
                  <Edit3 size={12} strokeWidth={1.5} />
                  Edit
                </button>
                <button
                  onClick={() => onRequestDelete(a)}
                  data-testid={`admin-article-delete-${a.id}`}
                  className="inline-flex items-center gap-2 border border-[#E2E8F0] hover:border-red-500 hover:text-red-600 px-3 py-2 font-mono text-[10px] uppercase tracking-widest-plus text-[#475569] transition-colors"
                >
                  <Trash2 size={12} strokeWidth={1.5} />
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </>
);

const BookingsTab = () => (
  <div
    data-testid="admin-bookings-info"
    className="bg-white border border-[#E2E8F0] p-10"
  >
    <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mb-3">
      Bookings — WhatsApp channel
    </div>
    <h3 className="font-serif text-2xl text-[#0A2540] mb-4">
      All patient bookings flow through WhatsApp.
    </h3>
    <p className="font-sans text-base text-[#475569] leading-relaxed max-w-2xl">
      To keep things simple for patients, consultation bookings are handled
      directly on your WhatsApp business number (<span className="text-[#0A2540]">+91 88713 30339</span>).
      Every &ldquo;Book via WhatsApp&rdquo; click on the site opens a chat with a
      pre-filled message — you receive and confirm slots directly on WhatsApp.
    </p>
    <p className="font-sans text-sm text-[#475569] leading-relaxed max-w-2xl mt-4">
      If you&rsquo;d like the site to also collect bookings via a form (name,
      contact, symptom, preferred date) and list them here in the console,
      let us know and we&rsquo;ll add that flow next to WhatsApp.
    </p>
  </div>
);

const ConfirmModal = ({ label, onCancel, onConfirm }) => (
  <div
    className="fixed inset-0 z-50 bg-[#0A2540]/70 backdrop-blur-sm flex items-center justify-center p-6"
    data-testid="admin-confirm-modal"
  >
    <div className="bg-white border border-[#E2E8F0] max-w-md w-full">
      <div className="p-6">
        <div className="font-mono text-[10px] uppercase tracking-widest-plus text-red-600 mb-2">
          Confirm delete
        </div>
        <h3 className="font-serif text-2xl text-[#0A2540]">
          Delete “{label}”?
        </h3>
        <p className="mt-2 font-sans text-sm text-[#475569]">
          This action cannot be undone.
        </p>
      </div>
      <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#E2E8F0] bg-[#F8FAFC]">
        <button
          onClick={onCancel}
          data-testid="admin-confirm-cancel"
          className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest-plus text-[#475569] hover:text-[#0A2540]"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          data-testid="admin-confirm-delete"
          className="inline-flex items-center gap-2 bg-red-600 text-white hover:bg-red-700 px-5 py-3 font-mono text-[10px] uppercase tracking-widest-plus transition-colors"
        >
          <Trash2 size={14} strokeWidth={1.5} />
          Delete permanently
        </button>
      </div>
    </div>
  </div>
);
